import React from 'react';

const AMDInstinct = () => (
  <article className="news-article-body">
    <p className="news-lead">
      AMD has emerged as the credible alternative to NVIDIA's data centre GPU monopoly.
      From the MI300X's record-setting 192 GB HBM3 memory pool to the MI355X's 2.6 exaflops
      rack, AMD's Instinct GPU roadmap — combined with its EPYC Turin CPU and the Lux
      sovereign AI supercomputer — shows a company that has closed the gap faster than
      the market expected.
    </p>

    <h2>Why AMD Matters in AI</h2>
    <p>
      NVIDIA dominates AI training with ~80–90% market share. But AMD's CDNA architecture
      GPUs are winning in specific segments: long-context inference (where massive HBM memory
      pools matter most), HPC scientific workloads, and deployments requiring ROCm's
      open-source software stack instead of CUDA lock-in.
    </p>

    <h2>The Instinct GPU Roadmap</h2>
    <div className="news-timeline">
      <div className="news-timeline-item">
        <span className="news-timeline-date">MI300X (Dec 2023)</span>
        <div>
          <strong>192 GB HBM3</strong> — industry's largest GPU memory at launch. 5.3 TB/s bandwidth.
          5,229 TFLOPS FP8. Eight-GPU nodes deliver 1.5 TB total HBM and 42 petaFLOPs.
          Immediately adopted by Microsoft Azure and Meta for long-context inference workloads.
        </div>
      </div>
      <div className="news-timeline-item">
        <span className="news-timeline-date">MI350X (Mid-2025)</span>
        <div>
          <strong>CDNA 4 architecture.</strong> 288 GB HBM3E, 8 TB/s bandwidth.
          4× gen-on-gen AI compute improvement. 35× inference performance vs MI300 series.
          Available in air-cooled (64 GPU) and liquid-cooled (128 GPU) rack configurations.
        </div>
      </div>
      <div className="news-timeline-item">
        <span className="news-timeline-date">MI355X DLC Rack</span>
        <div>
          128 GPUs. <strong>36 TB HBM3e</strong> total memory per rack.
          <strong> 2.6 exaflops</strong> peak throughput at FP4 precision.
          Largest single-rack HBM memory pool ever deployed.
        </div>
      </div>
      <div className="news-timeline-item">
        <span className="news-timeline-date">MI400 (2026)</span>
        <div>
          <strong>CDNA "Next" architecture.</strong> 432 GB HBM4. 19.6 TB/s bandwidth.
          40 petaflops FP4. 300 GB/s scale-out per GPU. AMD's direct answer to NVIDIA Vera Rubin.
        </div>
      </div>
    </div>

    <h2>The MI350X vs NVIDIA Blackwell</h2>
    <div className="news-specs-grid">
      <div className="news-spec-item">
        <span className="news-spec-label">AMD MI355X (DLC Rack)</span>
        <span className="news-spec-value">2.6 EFLOPS FP4 · 36 TB HBM3e · 128 GPUs</span>
      </div>
      <div className="news-spec-item">
        <span className="news-spec-label">NVIDIA Blackwell NVL72</span>
        <span className="news-spec-value">~720 PFLOPS FP4 · 72 GPUs</span>
      </div>
      <div className="news-spec-item">
        <span className="news-spec-label">AMD MI400 (2026)</span>
        <span className="news-spec-value">40 PFLOPS FP4 · 432 GB HBM4 per GPU</span>
      </div>
      <div className="news-spec-item">
        <span className="news-spec-label">NVIDIA Vera Rubin (2026)</span>
        <span className="news-spec-value">50 PFLOPS FP4 · 288 GB HBM4 per GPU</span>
      </div>
    </div>

    <h2>EPYC Turin — The CPU Side</h2>
    <p>
      AMD's 5th Gen EPYC CPU (codenamed <strong>"Turin"</strong>), launched October 2024,
      offers up to <strong>192 cores</strong> per socket — the highest core count in
      server CPU history. Turin pairs with Instinct GPUs in AI server nodes, acting as
      the orchestration and pre/post-processing engine while GPUs handle matrix computation.
    </p>
    <p>
      The follow-on "Venice" EPYC is planned to pair with the MI430X GPU in the 2026
      Discovery supercomputer.
    </p>

    <h2>The Lux & Discovery Supercomputers — US Sovereign AI</h2>
    <p>
      In October 2025, AMD announced two landmark supercomputer deployments at
      <strong>Oak Ridge National Laboratory (ORNL)</strong> — part of a
      <strong>$1 billion public-private investment</strong> to build US sovereign AI infrastructure:
    </p>
    <ul>
      <li>
        <strong>Lux</strong> (deploying early 2026): EPYC Turin + MI355X GPUs + AMD Pensando
        networking. America's first dedicated AI factory for science. Co-built with ORNL,
        AMD, Oracle Cloud Infrastructure, and HPE. Open-source orchestration with multi-tenant
        AI workflows.
      </li>
      <li>
        <strong>Discovery</strong>: EPYC "Venice" + MI430X GPUs + HPE Cray GX5000 architecture.
        Target domains: energy, medicine, materials science, advanced manufacturing.
      </li>
    </ul>
    <p>
      These deployments mark AMD as a primary supplier for US government AI infrastructure —
      a significant policy win that diversifies sovereign compute away from sole-source NVIDIA
      dependency.
    </p>

    <h2>The ROCm Advantage</h2>
    <p>
      AMD's <strong>ROCm</strong> (Radeon Open Compute) is a fully open-source GPU
      computing stack — the alternative to NVIDIA's proprietary CUDA. While CUDA's ecosystem
      dominance is real, ROCm has matured significantly: PyTorch, JAX, TensorFlow, and most
      major AI frameworks now support ROCm natively. For enterprises concerned about CUDA
      lock-in or requiring open-source supply chains for compliance, AMD's ROCm path has
      become genuinely viable.
    </p>

    <div className="news-insight-box">
      <h3>Key Takeaway</h3>
      <p>
        AMD's Instinct roadmap is the most serious structural challenge to NVIDIA's data
        centre AI dominance since the GPU race began. The MI355X's 2.6 exaflop DLC rack
        and the MI400's HBM4 specs show AMD is not content to occupy a niche — it is
        competing directly for the same hyperscale and sovereign AI contracts as NVIDIA.
        The ROCm open-source stack and US government deployments strengthen AMD's position
        as the credible, open alternative for AI infrastructure.
      </p>
    </div>
  </article>
);

export default AMDInstinct;
